// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/hooks/useActiveDelivery.ts
// 🔧 PURPOSE: La consegna in corso (caffè & brioche, vino, scorte), per il
//             widget in cima a tutte le pagine.
//
//             Un controllo al minuto con l'app davanti, niente con l'app in
//             tasca: al ritorno si ricontrolla subito. Le push di consegna
//             (App.tsx → `nva_delivery_update`) e quelle della chat
//             (`nva_chat_reaction`) ricaricano all'istante, senza aspettare
//             il minuto.
// ============================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchActiveDelivery, type ActiveDelivery } from '../lib/guestApi';

const POLL_MS = 60_000;
// Al ritorno nell'app arrivano quasi sempre sia `visibilitychange` sia
// `focus`: una richiesta sola per entrambi.
const REFETCH_GAP_MS = 2_000;

export function useActiveDelivery(token: string | null): ActiveDelivery | null {
  const [delivery, setDelivery] = useState<ActiveDelivery | null>(null);
  const seqRef = useRef(0);
  const lastFetchRef = useRef(0);

  const load = useCallback(async (force: boolean) => {
    if (!token) return;
    if (!force && Date.now() - lastFetchRef.current < REFETCH_GAP_MS) return;
    lastFetchRef.current = Date.now();
    const seq = ++seqRef.current;
    const result = await fetchActiveDelivery(token);
    // Una risposta arrivata dopo una piu' recente non riporta indietro il widget.
    if (seq !== seqRef.current) return;
    if (result.success) {
      setDelivery(result.delivery);
    } else if (result.errorCode === 'SESSION_EXPIRED' || (result.errorCode || '').indexOf('booking_') === 0) {
      // Sessione finita: il widget sparisce (il logout lo fa chi di dovere).
      setDelivery(null);
    }
    // Rete assente o endpoint non ancora pubblicato: si tiene quello che si
    // aveva, invece di far lampeggiare il widget a ogni giro.
  }, [token]);

  useEffect(() => {
    if (!token) {
      seqRef.current++;
      setDelivery(null);
      return;
    }

    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const start = () => {
      stop();
      timer = setInterval(() => {
        if (!document.hidden) load(true);
      }, POLL_MS);
    };
    const resume = () => {
      if (document.hidden) return;
      load(false);
      start();
    };
    const onVisibilityChange = () => {
      if (document.hidden) stop();
      else resume();
    };
    // Una push puo' portare due eventi (chat + consegna) e arrivare mentre
    // un giro e' gia' partito: si raccolgono in una richiesta sola, che
    // parte comunque (quella in corso potrebbe non vedere la novita').
    let pushTimer: ReturnType<typeof setTimeout> | null = null;
    const onPush = () => {
      if (pushTimer !== null) clearTimeout(pushTimer);
      pushTimer = setTimeout(() => {
        pushTimer = null;
        load(true);
      }, 300);
    };

    load(true);
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', resume);
    window.addEventListener('nva_delivery_update', onPush);
    window.addEventListener('nva_chat_reaction', onPush);

    return () => {
      stop();
      if (pushTimer !== null) clearTimeout(pushTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', resume);
      window.removeEventListener('nva_delivery_update', onPush);
      window.removeEventListener('nva_chat_reaction', onPush);
    };
  }, [token, load]);

  return delivery;
}

export default useActiveDelivery;
